import React, { useState, useEffect } from 'react';
import AITradeConfigSummaryModal from '../modals/AITradeConfigSummaryModal';

const AITradeTab = ({
  user,
  aiTradeStatus,
  setShowAiTradeModal,
  loading,
  setLoading,
  authenticatedFetch,
  fetchAiTradeStatus,
  fetchScheduledOrders,
  fetchWatchlist,
  watchlist,
  showWatchlist,
  setShowWatchlist,
  aiAnalyses,
  setAiAnalyses,
  selectedAnalysis,
  setSelectedAnalysis,
  showAnalysisDetail,
  setShowAnalysisDetail,
  setSelectedStock,
  setActiveTab,
  fetchStockData,
  fetchAiAnalyses
}) => {
  // 주단위 리포트 상태
  const [showWeeklyReport, setShowWeeklyReport] = useState(false);
  const [selectedWeeklyReport, setSelectedWeeklyReport] = useState(null);
  const [weeklyReports, setWeeklyReports] = useState([]);
  const [currentReportIndex, setCurrentReportIndex] = useState(0);
  
  // 분석 리포트 날짜 네비게이션 상태
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [groupedDates, setGroupedDates] = useState([]);
  
  // AI 자동거래 설정 설명글 표시/숨김 상태
  const [showConfigExplanation, setShowConfigExplanation] = useState(false);
  
  // 설정 요약 모달 상태
  const [showConfigSummaryModal, setShowConfigSummaryModal] = useState(false);
  const [analysisScheduleTime, setAnalysisScheduleTime] = useState('');

  const toLocalInputValue = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    const d = new Date(date);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  useEffect(() => {
    if (selectedAnalysis) {
      const baseTime = selectedAnalysis.trade_config?.scheduled_time
        ? new Date(selectedAnalysis.trade_config.scheduled_time)
        : new Date(Date.now() + 10 * 60 * 1000);
      setAnalysisScheduleTime(toLocalInputValue(baseTime));
    }
  }, [selectedAnalysis]);

  const handleScheduleFromAnalysis = async (analysis) => {
    if (!analysis?._id) return;
    if (!window.confirm('이 분석 결과로 예약 주문을 생성하시겠습니까?')) {
      return;
    }
    try {
      setLoading(true);
      const response = await authenticatedFetch(`/api/ai-trade/analyses/${analysis._id}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scheduled_time: analysisScheduleTime || undefined
        })
      });
      if (response.ok) {
        const data = await response.json();
        alert('예약 주문이 생성되었습니다.');
        if (typeof fetchAiAnalyses === 'function') {
          fetchAiAnalyses();
        }
        if (fetchScheduledOrders) {
          fetchScheduledOrders();
        }
        if (data.analysis) {
          setSelectedAnalysis(data.analysis);
        }
      } else {
        const error = await response.json();
        alert(error.error || '예약 주문 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('예약 주문 생성 실패:', error);
      alert('예약 주문 생성 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteFromAnalysis = async (analysis) => {
    if (!analysis?._id) return;
    if (!window.confirm('이 분석 결과로 즉시 주문을 실행하시겠습니까?')) {
      return;
    }
    try {
      setLoading(true);
      const response = await authenticatedFetch(`/api/ai-trade/analyses/${analysis._id}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: 120000 // 120초 - 주문 실행 및 KIS API 대기
      });
      if (response.ok) {
        const data = await response.json();
        alert('주문이 실행되었습니다.');
        if (typeof fetchAiAnalyses === 'function') {
          fetchAiAnalyses();
        }
        if (fetchScheduledOrders) {
          fetchScheduledOrders();
        }
        if (data.analysis) {
          setSelectedAnalysis(data.analysis);
        }
      } else {
        const error = await response.json();
        alert(error.error || '즉시 실행에 실패했습니다.');
      }
    } catch (error) {
      console.error('즉시 실행 실패:', error);
      alert('즉시 실행 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  // 주단위 리포트 조회
  useEffect(() => {
    if (showWeeklyReport) {
      fetchWeeklyReports();
    }
  }, [showWeeklyReport]);
  
  // AI 분석 리스트는 부모(Trading.js)에서 activeTab === 'ai-trade'일 때 이미 조회하므로
  // 마운트 시 중복 호출하지 않음 (fetchAiAnalyses 중복 완화)

  // 분석 리포트 날짜 그룹화
  useEffect(() => {
    if (aiAnalyses && aiAnalyses.length > 0) {
      const grouped = {};
      aiAnalyses.forEach(analysis => {
        // 날짜 파싱 개선: ISO 문자열 또는 타임스탬프 모두 처리
        let date;
        if (analysis.createdAt) {
          date = new Date(analysis.createdAt);
        } else if (analysis.updatedAt) {
          date = new Date(analysis.updatedAt);
        } else {
          date = new Date();
        }
        
        // 유효한 날짜인지 확인
        if (isNaN(date.getTime())) {
          date = new Date();
        }
        
        const dateKey = date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
        if (!grouped[dateKey]) {
          grouped[dateKey] = {
            dateKey,
            date: date,
            dateTimestamp: date.getTime(), // 정렬을 위한 타임스탬프 추가
            analyses: []
          };
        }
        grouped[dateKey].analyses.push(analysis);
      });
      
      // 날짜 타임스탬프로 정렬 (최신 날짜가 먼저)
      const sortedDates = Object.values(grouped).sort((a, b) => {
        const timeA = a.dateTimestamp || a.date.getTime() || 0;
        const timeB = b.dateTimestamp || b.date.getTime() || 0;
        return timeB - timeA; // 내림차순 (최신이 먼저)
      });
      
      setGroupedDates(sortedDates);
      // 최신 날짜(인덱스 0)로 초기화
      if (sortedDates.length > 0 && currentDateIndex >= sortedDates.length) {
        setCurrentDateIndex(0);
      }
    } else {
      setGroupedDates([]);
      setCurrentDateIndex(0);
    }
  }, [aiAnalyses]);
  
  const fetchWeeklyReports = async () => {
    try {
      const response = await authenticatedFetch('/api/ai-trade/weekly-reports');
      if (response.ok) {
        const data = await response.json();
        setWeeklyReports(data.reports || []);
        if (data.reports && data.reports.length > 0 && !selectedWeeklyReport) {
          setSelectedWeeklyReport(data.reports[0]);
          setCurrentReportIndex(0);
        }
      }
    } catch (error) {
      console.error('주단위 리포트 조회 실패:', error);
    }
  };
  
  const navigateWeeklyReport = (direction) => {
    if (weeklyReports.length === 0) return;
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentReportIndex < weeklyReports.length - 1 ? currentReportIndex + 1 : currentReportIndex;
    } else {
      newIndex = currentReportIndex > 0 ? currentReportIndex - 1 : currentReportIndex;
    }
    
    setCurrentReportIndex(newIndex);
    setSelectedWeeklyReport(weeklyReports[newIndex]);
  };
  
  const navigateAnalysisDate = (direction) => {
    if (groupedDates.length === 0) return;
    
    let newIndex;
    // 최신 날짜가 인덱스 0이므로:
    // "이전" = 더 오래된 날짜로 이동 (인덱스 증가)
    // "다음" = 더 최신 날짜로 이동 (인덱스 감소)
    if (direction === 'prev') {
      // 더 오래된 날짜로 이동 (인덱스 증가)
      newIndex = currentDateIndex < groupedDates.length - 1 ? currentDateIndex + 1 : currentDateIndex;
    } else if (direction === 'next') {
      // 더 최신 날짜로 이동 (인덱스 감소)
      newIndex = currentDateIndex > 0 ? currentDateIndex - 1 : currentDateIndex;
    } else {
      newIndex = currentDateIndex;
    }
    
    setCurrentDateIndex(newIndex);
  };
  return (
    <div className="ai-trade-tab" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>🤖 AI 자동거래 설정</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={async () => {
              if (!window.confirm('주단위 보고 리포트를 생성하시겠습니까?')) {
                return;
              }
              try {
                setLoading(true);
                const response = await authenticatedFetch('/api/ai-trade/weekly-report', {
                  method: 'POST'
                });
                if (response.ok) {
                  const data = await response.json();
                  alert(data.message || '주단위 리포트가 생성되었습니다.');
                  // 리포트 보기로 이동
                  setShowWeeklyReport(true);
                  setSelectedWeeklyReport(data.report);
                } else {
                  const error = await response.json();
                  alert(error.error || '주단위 리포트 생성에 실패했습니다.');
                }
              } catch (error) {
                console.error('주단위 리포트 생성 실패:', error);
                alert('주단위 리포트 생성 중 오류가 발생했습니다.');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: loading ? '#ccc' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            📊 정기보고
          </button>
        <button
          onClick={() => setShowAiTradeModal(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          ⚙️ 설정
        </button>
        <button
          onClick={() => setShowConfigSummaryModal(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          📋 설정요약
        </button>
        </div>
      </div>

      {/* AI 거래 상태 */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: aiTradeStatus.is_active ? '#d4edda' : '#f8f9fa', borderRadius: '8px', border: `2px solid ${aiTradeStatus.is_active ? '#28a745' : '#dee2e6'}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 10px 0' }}>
              {aiTradeStatus.is_active ? '✅ AI 자동거래 활성화' : '⏸️ AI 자동거래 비활성화'}
            </h3>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {aiTradeStatus.has_api_key ? (
                <span>
                  ✅ Google AI API 키 설정됨
                  {aiTradeStatus.using_env_key && (
                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#28a745', fontWeight: '600' }}>
                      (관리자 .env 키 사용)
                    </span>
                  )}
                </span>
              ) : (
                <span>⚠️ Google AI API 키가 설정되지 않았습니다.</span>
              )}
            </div>
            {aiTradeStatus.is_active && (
              <div style={{ fontSize: '13px', color: '#666', marginTop: '10px' }}>
                <div>분석 종목 수: {aiTradeStatus.symbol_count}개</div>
                {aiTradeStatus.last_analysis_time && (
                  <div>마지막 분석: {new Date(aiTradeStatus.last_analysis_time).toLocaleString('ko-KR')}</div>
                )}
                {aiTradeStatus.next_update_time && (
                  <div>다음 갱신: {new Date(aiTradeStatus.next_update_time).toLocaleString('ko-KR')}</div>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => setShowConfigExplanation(!showConfigExplanation)}
            style={{
              padding: '6px 12px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              marginLeft: '10px'
            }}
            title={showConfigExplanation ? '설명 숨기기' : '설명 보기'}
          >
            {showConfigExplanation ? '▲ 숨기기' : '▼ 설명'}
          </button>
        </div>
        
        {/* AI 자동거래 설정 설명글 */}
        {showConfigExplanation && (
          <div style={{ 
            marginTop: '15px', 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px', 
            border: '1px solid #dee2e6',
            fontSize: '13px',
            lineHeight: '1.8',
            color: '#333'
          }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600', color: '#495057' }}>
              📖 AI 자동거래 설정 및 상태 설명
            </h4>
            
            <div style={{ marginBottom: '15px' }}>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                ✅ AI 자동거래 활성화
              </h5>
              <p style={{ margin: 0, color: '#666' }}>
                AI 자동거래 기능이 활성화되어 있으면, 설정된 종목에 대해 자동으로 AI 분석을 수행하고 매수/매도 추천을 제공합니다. 
                비활성화되어 있으면 AI 분석 및 자동 거래가 실행되지 않습니다.
              </p>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                ✅ Google AI API 키 설정됨
              </h5>
              <p style={{ margin: 0, color: '#666' }}>
                Google AI (Gemini) API 키가 설정되어 있으면 고급 AI 분석 기능을 사용할 수 있습니다. 
                API 키가 없으면 로컬 AI만 사용됩니다. Google AI는 더 정확하고 심층적인 분석을 제공하지만 유료 서비스입니다.
              </p>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                📊 분석 종목 수
              </h5>
              <p style={{ margin: 0, color: '#666' }}>
                현재 AI 분석 대상으로 등록된 종목의 개수입니다. 이 종목들에 대해 주기적으로 AI 분석이 수행되며, 
                매수/매도 추천이 생성됩니다.
              </p>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                🕐 마지막 분석
              </h5>
              <p style={{ margin: 0, color: '#666' }}>
                가장 최근에 AI 분석이 수행된 시각입니다. 분석이 완료되면 이 시간이 업데이트됩니다.
              </p>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                🔄 다음 갱신
              </h5>
              <p style={{ margin: 0, color: '#666' }}>
                다음 AI 분석이 자동으로 실행될 예정 시각입니다. 설정된 갱신 주기에 따라 자동으로 분석이 수행됩니다.
              </p>
            </div>
            
            <div style={{ 
              marginTop: '20px', 
              padding: '12px', 
              backgroundColor: '#e7f3ff', 
              borderRadius: '6px', 
              border: '1px solid #b3d9ff' 
            }}>
              <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#0056b3' }}>
                💡 매수/매도 추천 이해하기
              </h5>
              <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.8' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>매수 추천:</strong> AI가 종목의 상승 가능성을 분석하여 매수할 것을 권장합니다. 
                  확신도가 높을수록 더 강한 매수 신호입니다. 설정에서 자동 매수가 활성화되어 있으면 자동으로 주문이 생성됩니다.
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>매도 추천:</strong> AI가 종목의 하락 위험을 분석하여 매도할 것을 권장합니다. 
                  보유 수량이 있어야 매도 주문이 생성됩니다. 보유 수량이 없으면 '관찰중' 상태로 표시됩니다.
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>보유 추천:</strong> AI가 현재 가격대에서 보유를 유지하는 것이 좋다고 판단합니다. 
                  추가 매수나 매도 없이 현재 포지션을 유지합니다.
                </div>
                <div>
                  <strong>확신도:</strong> AI의 추천에 대한 신뢰도를 백분율로 표시합니다. 
                  높은 확신도(80% 이상)는 강한 신호, 낮은 확신도(50% 미만)는 약한 신호로 해석할 수 있습니다.
                </div>
              </div>
            </div>
            
            <div style={{ 
              marginTop: '15px', 
              padding: '12px', 
              backgroundColor: '#fff3cd', 
              borderRadius: '6px', 
              border: '1px solid #ffc107' 
            }}>
              <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#856404' }}>
                ⚙️ 설정 관리
              </h5>
              <p style={{ margin: 0, fontSize: '12px', color: '#856404', lineHeight: '1.8' }}>
                상단의 <strong>⚙️ 설정</strong> 버튼을 클릭하면 AI 자동거래의 상세 설정을 변경할 수 있습니다. 
                자동 매수/매도 활성화, 확신도 임계값, 수량 비율, 잔고 제한 등을 설정할 수 있습니다.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* AI 거래 안내 */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #b3d9ff' }}>
        <h3 style={{ marginTop: 0 }}>📊 AI 자동거래 기능</h3>
        <ul style={{ lineHeight: '1.8', fontSize: '14px' }}>
          <li>Google Gemini AI를 활용한 심층 주식 분석</li>
          <li>1년간의 주가 데이터, 기업 실적, 뉴스, 공시 자료 종합 분석</li>
          <li>웹 검색을 통한 최신 시장 동향 반영</li>
          <li>주단위 자동 분석 및 예약 주문 갱신</li>
          <li>장기 투자 관점의 매수/매도 추천</li>
        </ul>
      </div>

      {/* 포트폴리오 분석 및 종목 발굴 버튼 */}
      {aiTradeStatus.is_active && aiTradeStatus.has_api_key && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #b3d9ff' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#333' }}>
              🎯 다양한 종목 추천 확보
            </h4>
            <p style={{ margin: 0, fontSize: '13px', color: '#666', lineHeight: '1.5' }}>
              AI가 새로운 종목을 발굴하거나 분석되지 않은 종목을 샘플링하여 다양성을 확보할 수 있습니다.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={async () => {
                // Google AI 사용 알림 모달 표시
                const showGoogleAIWarning = () => {
                  return new Promise((resolve) => {
                    const modal = document.createElement('div');
                    modal.style.cssText = `
                      position: fixed;
                      top: 0;
                      left: 0;
                      right: 0;
                      bottom: 0;
                      background: rgba(0, 0, 0, 0.5);
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      z-index: 10000;
                    `;
                    
                    const modalContent = document.createElement('div');
                    modalContent.style.cssText = `
                      background: white;
                      padding: 30px;
                      borderRadius: 12px;
                      max-width: 500px;
                      width: 90%;
                      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                    `;
                    
                    modalContent.innerHTML = `
                      <div style="margin-bottom: 20px;">
                        <h2 style="margin: 0 0 15px 0; fontSize: 20px; fontWeight: bold; color: #333;">
                          🔵 Google AI 사용 알림
                        </h2>
                        <div style="padding: 15px; background: #e7f3ff; borderRadius: 8px; marginBottom: 15px;">
                          <p style="margin: 0 0 10px 0; fontSize: 14px; color: #333; lineHeight: 1.6;">
                            포트폴리오 전체 AI 분석은 <strong style="color: #dc3545;">Google AI (유료)</strong>를 사용합니다.
                          </p>
                          <div style="fontSize: 13px; color: #666; lineHeight: 1.8;">
                            <div>📊 <strong>예상 사용량:</strong></div>
                            <div style="marginLeft: 20px; marginTop: 5px;">
                              • 종목당 약 5,000~10,000 토큰 사용<br/>
                              • 보유 종목 수에 따라 비용 발생<br/>
                              • Gemini 2.5 Flash 기준: 입력 $0.075/1M, 출력 $0.30/1M
                            </div>
                            <div style="marginTop: 10px; paddingTop: 10px; borderTop: 1px solid #b3d9ff;">
                              <strong style="color: #dc3545;">💡 참고:</strong> 분석은 시간이 다소 걸릴 수 있으며, 각 종목 분석 간 3초 대기 시간이 있습니다.
                            </div>
                          </div>
                        </div>
                      </div>
                      <div style="display: flex; gap: 10px; justifyContent: flex-end;">
                        <button id="cancelBtn" style="
                          padding: 10px 20px;
                          background: #6c757d;
                          color: white;
                          border: none;
                          borderRadius: 6px;
                          cursor: pointer;
                          fontSize: 14px;
                          fontWeight: 600;
                        ">취소</button>
                        <button id="confirmBtn" style="
                          padding: 10px 20px;
                          background: #667eea;
                          color: white;
                          border: none;
                          borderRadius: 6px;
                          cursor: pointer;
                          fontSize: 14px;
                          fontWeight: 600;
                        ">확인 및 분석 시작</button>
                      </div>
                    `;
                    
                    modal.appendChild(modalContent);
                    document.body.appendChild(modal);
                    
                    const cancelBtn = modalContent.querySelector('#cancelBtn');
                    const confirmBtn = modalContent.querySelector('#confirmBtn');
                    
                    const close = (result) => {
                      document.body.removeChild(modal);
                      resolve(result);
                    };
                    
                    cancelBtn.onclick = () => close(false);
                    confirmBtn.onclick = () => close(true);
                    modal.onclick = (e) => {
                      if (e.target === modal) close(false);
                    };
                  });
                };
                
                const confirmed = await showGoogleAIWarning();
                if (!confirmed) {
                  return;
                }
                
                let asyncPolling = false;
                try {
                  setLoading(true);
                  const response = await authenticatedFetch('/api/ai-trade/analyze-portfolio', {
                    method: 'POST',
                    timeout: 15000 // 202 수신용 짧은 타임아웃
                  });
                  const data = await response.json().catch(() => ({}));
                  if (response.status === 202 && data.job_id) {
                    asyncPolling = true;
                    const jobId = data.job_id;
                    const pollInterval = 2000;
                    const maxWait = 120000; // 120초
                    const start = Date.now();
                    const poll = async () => {
                      if (Date.now() - start > maxWait) {
                        setLoading(false);
                        alert('분석 진행 시간이 초과되었습니다. 잠시 후 AI 분석 결과를 새로고침해 보세요.');
                        return;
                      }
                      try {
                        const statusRes = await authenticatedFetch(`/api/ai-trade/analyze-portfolio/status/${jobId}`, { timeout: 10000 });
                        const statusData = await statusRes.json().catch(() => ({}));
                        if (statusData.status === 'completed') {
                          setLoading(false);
                          alert(statusData.message || '포트폴리오 AI 분석이 완료되었습니다.');
                          await fetchAiTradeStatus();
                          await fetchScheduledOrders();
                          await fetchAiAnalyses();
                          return;
                        }
                        if (statusData.status === 'failed') {
                          setLoading(false);
                          alert(statusData.message || '포트폴리오 분석에 실패했습니다.');
                          return;
                        }
                        setTimeout(poll, pollInterval);
                      } catch (e) {
                        setLoading(false);
                        alert('진행 상황 조회 중 오류가 발생했습니다. 잠시 후 AI 분석 결과를 새로고침해 보세요.');
                      }
                    };
                    setTimeout(poll, pollInterval);
                    return;
                  }
                  if (response.ok) {
                    alert(data.message || '포트폴리오 AI 분석이 완료되었습니다.');
                    await fetchAiTradeStatus();
                    await fetchScheduledOrders();
                    await fetchAiAnalyses();
                  } else {
                    alert(data.error || '포트폴리오 분석에 실패했습니다.');
                  }
                } catch (error) {
                  console.error('포트폴리오 분석 실패:', error);
                  if (error.name === 'TimeoutError' || (error.message && error.message.includes('초과'))) {
                    alert('요청 시간이 초과되었습니다. 서버가 바쁠 수 있습니다. 잠시 후 다시 시도해주세요.');
                  } else {
                    alert('포트폴리오 분석 중 오류가 발생했습니다.');
                  }
                } finally {
                  if (!asyncPolling) setLoading(false);
                }
              }}
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#ccc' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                flex: 1,
                minWidth: '200px'
              }}
            >
              {loading ? '분석 중...' : '📊 포트폴리오 AI 분석'}
            </button>
            <button
              onClick={async () => {
                if (!window.confirm('AI가 새로운 추천 종목을 발굴하고 자동으로 추가하시겠습니까? 시간이 다소 걸릴 수 있습니다.')) {
                  return;
                }
                try {
                  setLoading(true);
                  const response = await authenticatedFetch('/api/ai-trade/discover-and-add', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      max_results: 5
                    }),
                    timeout: 120000 // Google AI 호출로 인해 120초 타임아웃
                  });
                  if (response.ok) {
                    const data = await response.json();
                    alert(data.message || `${data.discovered}개의 새로운 종목을 발굴했습니다.`);
                    await fetchAiTradeStatus();
                    await fetchWatchlist();
                    await fetchAiAnalyses();
                  } else {
                    const error = await response.json();
                    alert(error.error || '종목 발굴에 실패했습니다.');
                  }
                } catch (error) {
                  console.error('종목 발굴 실패:', error);
                  if (error.name === 'TimeoutError' || error.status === 504) {
                    alert('요청 시간이 초과되었습니다. Google AI 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요.');
                  } else if (error.message && error.message.includes('Failed to fetch')) {
                    alert('네트워크 연결에 문제가 있습니다. 인터넷 연결을 확인하고 다시 시도해주세요.');
                  } else {
                    alert('종목 발굴 중 오류가 발생했습니다: ' + (error.message || '알 수 없는 오류'));
                  }
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#ccc' : '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                flex: 1,
                minWidth: '200px'
              }}
              title="Google AI를 사용하여 새로운 종목을 추천하고 자동으로 추가합니다"
            >
              {loading ? '발굴 중...' : '✨ 새로운 종목 발굴/추가'}
            </button>
            <button
              onClick={async () => {
                if (!window.confirm('분석되지 않은 종목을 샘플링하고 즉시 분석하시겠습니까? 시간이 다소 걸릴 수 있습니다.')) {
                  return;
                }
                try {
                  setLoading(true);
                  const response = await authenticatedFetch('/api/ai-trade/sample-unanalyzed', {
                    timeout: 30000, // 30초 타임아웃
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      max_results: 3,
                      analyze_immediately: false // 타임아웃 방지를 위해 즉시 분석 비활성화
                    }),
                    timeout: 30000 // 30초 타임아웃
                  });
                  if (response.ok) {
                    const data = await response.json();
                    alert(data.message || `${data.sampled}개 종목을 샘플링했습니다. 분석은 자동으로 진행됩니다.`);
                    await fetchAiTradeStatus();
                    await fetchAiAnalyses();
                  } else {
                    const error = await response.json();
                    alert(error.error || '종목 샘플링에 실패했습니다.');
                  }
                } catch (error) {
                  console.error('종목 샘플링 실패:', error);
                  if (error.name === 'TimeoutError' || error.status === 504) {
                    alert('요청 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.');
                  } else if (error.message && error.message.includes('Failed to fetch')) {
                    alert('네트워크 연결에 문제가 있습니다. 인터넷 연결을 확인하고 다시 시도해주세요.');
                  } else {
                    alert('종목 샘플링 중 오류가 발생했습니다: ' + (error.message || '알 수 없는 오류'));
                  }
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#ccc' : '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                flex: 1,
                minWidth: '200px'
              }}
              title="분석되지 않은 종목을 랜덤 샘플링하여 다양성을 확보합니다"
            >
              {loading ? '샘플링 중...' : '🎲 미분석 종목 샘플링'}
            </button>
            <button
              onClick={async () => {
                if (!window.confirm('AI가 새로운 추천 종목을 발굴하시겠습니까? 시간이 다소 걸릴 수 있습니다.')) {
                  return;
                }
                try {
                  setLoading(true);
                  const response = await authenticatedFetch('/api/ai-trade/discover', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      criteria: {},
                      max_results: 10
                    })
                  });
                  if (response.ok) {
                    // Content-Type 확인
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                      const data = await response.json();
                      if (data.recommendations && data.recommendations.length > 0) {
                        // 추천 종목을 관심종목에 추가할지 확인
                        const addToWatchlist = window.confirm(
                          `${data.recommendations.length}개의 추천 종목을 발견했습니다.\n관심종목에 추가하시겠습니까?`
                        );
                        if (addToWatchlist) {
                          // 각 추천 종목을 관심종목에 추가
                          let successCount = 0;
                          let failCount = 0;
                          for (const rec of data.recommendations) {
                            try {
                              const addResponse = await authenticatedFetch('/api/watchlist/ai-recommend', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  symbol: rec.symbol,
                                  stock_name: rec.stock_name,
                                  confidence: rec.confidence,
                                  reasoning: rec.reasoning
                                })
                              });
                              if (addResponse.ok) {
                                successCount++;
                              } else {
                                failCount++;
                                const errorData = await addResponse.json();
                                console.error(`관심종목 추가 실패: ${rec.symbol}`, errorData);
                              }
                            } catch (error) {
                              failCount++;
                              console.error(`관심종목 추가 실패: ${rec.symbol}`, error);
                            }
                          }
                          if (successCount > 0) {
                            alert(`${successCount}개의 추천 종목이 관심종목에 추가되었습니다.${failCount > 0 ? ` (${failCount}개 실패)` : ''}`);
                            // 관심종목 리스트 새로고침
                            await fetchWatchlist();
                          } else {
                            alert('추천 종목 추가에 실패했습니다.');
                          }
                        } else {
                          alert(data.message || `${data.recommendations.length}개의 추천 종목을 발견했습니다.`);
                        }
                      } else {
                        alert('추천 종목을 찾지 못했습니다.');
                      }
                    } else {
                      // HTML 응답인 경우
                      const text = await response.text();
                      console.error('AI 추천 종목 발굴 실패: HTML 응답 수신', {
                        status: response.status,
                        contentType,
                        preview: text.substring(0, 200)
                      });
                      alert('서버 응답 오류가 발생했습니다. nginx 설정을 확인해주세요.');
                    }
                  } else {
                    // 에러 응답 처리
                    try {
                      const contentType = response.headers.get('content-type');
                      if (contentType && contentType.includes('application/json')) {
                        const error = await response.json();
                        alert(error.error || '종목 발굴에 실패했습니다.');
                      } else {
                        const text = await response.text();
                        console.error('AI 추천 종목 발굴 실패: 비JSON 응답', {
                          status: response.status,
                          preview: text.substring(0, 200)
                        });
                        alert(`서버 오류가 발생했습니다. (${response.status})`);
                      }
                    } catch (parseError) {
                      console.error('에러 응답 파싱 실패:', parseError);
                      alert(`서버 오류가 발생했습니다. (${response.status})`);
                    }
                  }
                } catch (error) {
                  console.error('종목 발굴 실패:', error);
                  if (error.message && error.message.includes('Unexpected token')) {
                    alert('서버 응답 형식 오류가 발생했습니다. nginx 설정을 확인해주세요.');
                  } else {
                    alert('종목 발굴 중 오류가 발생했습니다.');
                  }
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#ccc' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                flex: 1,
                minWidth: '200px'
              }}
              title="AI 추천 종목을 조회합니다 (수동으로 관심종목에 추가 가능)"
            >
              {loading ? '발굴 중...' : '🔍 AI 추천 조회'}
            </button>
          </div>
        </div>
      )}

      {/* 관심종목 섹션 */}
      <div style={{ marginTop: '30px', marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0 }}>⭐ 관심종목</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setShowWatchlist(!showWatchlist)}
              style={{
                padding: '8px 16px',
                backgroundColor: showWatchlist ? '#6c757d' : '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {showWatchlist ? '숨기기' : '보기'}
            </button>
            <button
              onClick={fetchWatchlist}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              🔄 새로고침
            </button>
          </div>
        </div>

        {showWatchlist && (
          <div style={{ marginTop: '15px' }}>
            {watchlist.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <div>관심종목이 없습니다.</div>
                <div style={{ fontSize: '13px', marginTop: '10px', color: '#999' }}>
                  AI 추천 종목 발굴을 실행하거나 수동으로 추가할 수 있습니다.
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {watchlist.map((item, index) => (
                  <div
                    key={item._id || index}
                    style={{
                      padding: '15px',
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      border: '1px solid #dee2e6',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '5px' }}>
                          {item.stock_name || item.symbol} ({item.symbol})
                        </div>
                        <div style={{ display: 'flex', gap: '10px', fontSize: '13px', color: '#666', flexWrap: 'wrap' }}>
                          <span>
                            상태: 
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '3px',
                              backgroundColor: item.status === '매수예정' ? '#d4edda' :
                                             item.status === '검토중' ? '#fff3cd' :
                                             item.status === '보류' ? '#f8d7da' : '#e2e3e5',
                              color: item.status === '매수예정' ? '#155724' :
                                    item.status === '검토중' ? '#856404' :
                                    item.status === '보류' ? '#721c24' : '#383d41',
                              fontWeight: '600',
                              marginLeft: '5px'
                            }}>
                              {item.status}
                            </span>
                          </span>
                          {item.is_ai_recommended && (
                            <span>
                              AI 추천: 
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '3px',
                                backgroundColor: '#667eea',
                                color: 'white',
                                fontWeight: '600',
                                marginLeft: '5px'
                              }}>
                                {item.ai_recommendation?.confidence || 0}%
                              </span>
                            </span>
                          )}
                          {item.target_price > 0 && (
                            <span>목표가: ₩{item.target_price.toLocaleString()}</span>
                          )}
                        </div>
                        {item.reason && (
                          <div style={{ marginTop: '8px', fontSize: '13px', color: '#555' }}>
                            <strong>추가 이유:</strong> {item.reason}
                          </div>
                        )}
                        {item.ai_recommendation?.reasoning && (
                          <div style={{ marginTop: '8px', fontSize: '13px', color: '#555', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                            <strong>AI 추천 이유:</strong> {item.ai_recommendation.reasoning}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                        <button
                          onClick={async () => {
                            try {
                              const response = await authenticatedFetch(`/api/watchlist/${item._id}/analyze`, {
                                method: 'POST'
                              });
                              if (response.ok) {
                                alert('AI 분석이 완료되었습니다.');
                                await fetchWatchlist();
                                await fetchAiAnalyses();
                              } else {
                                const error = await response.json();
                                alert(error.error || 'AI 분석에 실패했습니다.');
                              }
                            } catch (error) {
                              console.error('AI 분석 실패:', error);
                              alert('AI 분석 중 오류가 발생했습니다.');
                            }
                          }}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            backgroundColor: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          🤖 AI 분석
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm('관심종목에서 삭제하시겠습니까?')) {
                              return;
                            }
                            try {
                              const response = await authenticatedFetch(`/api/watchlist/${item._id}`, {
                                method: 'DELETE'
                              });
                              if (response.ok) {
                                alert('관심종목이 삭제되었습니다.');
                                await fetchWatchlist();
                              } else {
                                const error = await response.json();
                                alert(error.error || '삭제에 실패했습니다.');
                              }
                            } catch (error) {
                              console.error('관심종목 삭제 실패:', error);
                              alert('삭제 중 오류가 발생했습니다.');
                            }
                          }}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI 거래 분석 리스트 */}
      <div style={{ marginTop: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0 }}>📋 AI 거래 분석 리스트</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => {
                // AI 분석 종목을 거래 탭으로 이동
                setActiveTab('trade');
              }}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title="AI 분석 종목을 거래 탭에서 확인"
            >
              📊 거래 탭에서 보기
            </button>
            <button
              onClick={async () => {
                // fetchAiAnalyses 함수를 사용하여 일관된 상태 관리
                if (fetchAiAnalyses && typeof fetchAiAnalyses === 'function') {
                  try {
                    await fetchAiAnalyses();
                  } catch (error) {
                    console.error('AI 분석 리스트 조회 오류:', error);
                  }
                } else {
                  // fallback: 직접 API 호출
                  try {
                    const response = await authenticatedFetch('/api/ai-trade/analyses', {
                      timeout: 120000 // 120초 타임아웃
                    });
                    if (response.ok) {
                      const data = await response.json();
                      setAiAnalyses(data.analyses || []);
                    } else {
                      console.error('AI 분석 리스트 조회 실패:', response.status);
                    }
                  } catch (error) {
                    console.error('AI 분석 리스트 조회 오류:', error);
                  }
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              🔄 새로고침
            </button>
          </div>
        </div>

        <div className="ai-trade-analysis-list-wrap" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
        {aiAnalyses.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>
              📊 AI 분석 결과가 없습니다
            </div>
            <div style={{ fontSize: '13px', marginTop: '10px', color: '#999', lineHeight: '1.6' }}>
              <div>AI 분석은 다음 방법으로 실행할 수 있습니다:</div>
              <div style={{ marginTop: '10px', textAlign: 'left', display: 'inline-block' }}>
                <div>1. <strong>자동 분석</strong>: AI 스케줄러가 자동으로 실행</div>
                <div style={{ marginLeft: '20px', fontSize: '12px', color: '#666' }}>
                  - 매일 장 아침 6시: Local AI 분석
                  <br />
                  - 매월 첫째 3주차 영업일: 정기 분석 (Gemini 1.5 Pro)
                  <br />
                  - 분기별 첫 영업일: 분기별 분석 (Gemini 1.5 Pro)
                </div>
                <div style={{ marginTop: '8px' }}>2. <strong>수동 분석</strong>: AI 거래 설정 모달에서 포트폴리오 분석 실행</div>
                <div style={{ marginTop: '8px' }}>3. <strong>즉시 분석</strong>: 주문 탭에서 종목 선택 후 AI 분석 요청</div>
              </div>
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '5px', fontSize: '12px', color: '#856404' }}>
                💡 AI 분석을 실행하려면 AI 거래 설정에서 Google AI API 키가 설정되어 있어야 합니다.
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* 날짜 네비게이션 */}
            {groupedDates.length > 0 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '15px'
              }}>
                <button
                  onClick={() => navigateAnalysisDate('prev')}
                  disabled={currentDateIndex >= groupedDates.length - 1}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: currentDateIndex >= groupedDates.length - 1 ? '#ccc' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: currentDateIndex >= groupedDates.length - 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                  title="더 오래된 날짜로 이동"
                >
                  ← 이전
                </button>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                  📅 {groupedDates[currentDateIndex]?.dateKey || '날짜 없음'} 
                  ({currentDateIndex + 1} / {groupedDates.length})
                </div>
                <button
                  onClick={() => navigateAnalysisDate('next')}
                  disabled={currentDateIndex === 0}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: currentDateIndex === 0 ? '#ccc' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: currentDateIndex === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                  title="더 최신 날짜로 이동"
                >
                  다음 →
                </button>
              </div>
            )}

            {/* 현재 선택된 날짜의 분석 결과 표시 */}
            {groupedDates.length > 0 && groupedDates[currentDateIndex] && (
              <div key={groupedDates[currentDateIndex].dateKey} style={{ marginBottom: '30px' }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold', 
                    marginBottom: '15px', 
                    paddingBottom: '10px',
                    borderBottom: '2px solid #667eea',
                    color: '#333'
                  }}>
                  📅 {groupedDates[currentDateIndex].dateKey}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {groupedDates[currentDateIndex].analyses
                      .sort((a, b) => new Date(b.createdAt || b.updatedAt) - new Date(a.createdAt || a.updatedAt))
                      .map((analysis, index) => (
                        <div
                          key={analysis._id || index}
                          onClick={() => {
                            setSelectedAnalysis(analysis);
                            setShowAnalysisDetail(true);
                          }}
                          style={{
                            padding: '15px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.borderColor = '#667eea';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff';
                            e.currentTarget.style.borderColor = '#dee2e6';
                          }}
                        >
                          {/* AI 거래 분석 배지 */}
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            padding: '4px 8px',
                            fontSize: '11px',
                            backgroundColor: '#667eea',
                            color: 'white',
                            borderRadius: '4px',
                            fontWeight: 'bold'
                          }}>
                            🤖 AI 분석
                          </div>
                          
                          {/* 거래 탭으로 이동 버튼 */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedStock(analysis.symbol);
                              setActiveTab('trade');
                              // 종목 데이터 조회
                              fetchStockData(analysis.symbol);
                            }}
                            style={{
                              position: 'absolute',
                              bottom: '10px',
                              right: '10px',
                              padding: '4px 8px',
                              fontSize: '11px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                            title="이 종목을 거래 탭에서 확인"
                          >
                            📊 거래하기
                          </button>
                          
                          {/* 한줄 요약 */}
                          <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '8px', color: '#333', paddingRight: '100px' }}>
                            {analysis.summary || `${analysis.stock_name || analysis.symbol} - 분석 결과`}
                          </div>

                          {/* 종목명 및 상태 */}
                          <div style={{ display: 'flex', gap: '15px', fontSize: '13px', color: '#666', marginBottom: '8px', flexWrap: 'wrap' }}>
                            <span>
                              <strong>종목:</strong> {analysis.stock_name || analysis.symbol} ({analysis.symbol})
                            </span>
                            <span>
                              <strong>상태:</strong> 
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '3px',
                                backgroundColor: analysis.current_status === '보유중' ? '#d4edda' :
                                               analysis.current_status === '구매예정' ? '#fff3cd' :
                                               analysis.current_status === '매도예정' ? '#f8d7da' : '#e2e3e5',
                                color: analysis.current_status === '보유중' ? '#155724' :
                                      analysis.current_status === '구매예정' ? '#856404' :
                                      analysis.current_status === '매도예정' ? '#721c24' : '#383d41',
                                fontWeight: '600',
                                marginLeft: '5px'
                              }}>
                                {analysis.current_status || '관찰중'}
                              </span>
                            </span>
                            <span>
                              <strong>추천:</strong> 
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '3px',
                                backgroundColor: analysis.recommendation?.action === 'buy' ? '#d4edda' :
                                               analysis.recommendation?.action === 'sell' ? '#f8d7da' : '#e2e3e5',
                                color: analysis.recommendation?.action === 'buy' ? '#155724' :
                                      analysis.recommendation?.action === 'sell' ? '#721c24' : '#383d41',
                                fontWeight: '600',
                                marginLeft: '5px'
                              }}>
                                {
                                  analysis.recommendation?.action === 'buy' ? '매수' :
                                  analysis.recommendation?.action === 'sell' ? '매도' :
                                  '보유'
                                } ({analysis.recommendation?.confidence || 0}%)
                              </span>
                            </span>
                          </div>

                          {/* 거래 설정 */}
                          {analysis.trade_config && analysis.trade_config.order_type !== 'none' && (
                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                              <strong>거래 설정:</strong> {
                                analysis.trade_config.order_type === 'buy' ? '매수' : '매도'
                              } {analysis.trade_config.quantity}주 @ ₩{analysis.trade_config.price?.toLocaleString() || '0'}
                              {analysis.trade_config.scheduled_time && (
                                <span> ({new Date(analysis.trade_config.scheduled_time).toLocaleString('ko-KR')})</span>
                              )}
                            </div>
                          )}

                          {/* 분석 시간 및 다음 분석 시간 */}
                          <div style={{ display: 'flex', gap: '15px', fontSize: '12px', color: '#999', marginTop: '8px', flexWrap: 'wrap' }}>
                            <span>
                              분석 시간: {new Date(analysis.createdAt).toLocaleString('ko-KR')}
                            </span>
                            {analysis.next_analysis_time && (
                              <span>
                                다음 분석: {new Date(analysis.next_analysis_time).toLocaleString('ko-KR')}
                              </span>
                            )}
                          </div>
                          
                          {/* 추천 이유 미리보기 */}
                          {analysis.recommendation?.reasoning && (
                            <div style={{ 
                              marginTop: '8px', 
                              padding: '10px', 
                              backgroundColor: '#f8f9fa', 
                              borderRadius: '4px',
                              fontSize: '13px',
                              color: '#555',
                              maxHeight: '60px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              <strong>추천 이유:</strong> {typeof analysis.recommendation.reasoning === 'string' 
                                ? analysis.recommendation.reasoning.substring(0, 100) + (analysis.recommendation.reasoning.length > 100 ? '...' : '')
                                : '상세 내용은 클릭하여 확인하세요'}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
            )}
          </div>
        )}
        </div>
      </div>
      
      {/* 주단위 리포트 모달 */}
      {showWeeklyReport && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 100012,
            padding: '20px'
          }}
          onClick={() => setShowWeeklyReport(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '25px',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              position: 'relative',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowWeeklyReport(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>
            
            {selectedWeeklyReport ? (
              <>
                {/* 리포트 네비게이션 */}
                {weeklyReports.length > 1 && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '20px',
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '5px'
                  }}>
                    <button
                      onClick={() => navigateWeeklyReport('prev')}
                      disabled={currentReportIndex >= weeklyReports.length - 1}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: currentReportIndex >= weeklyReports.length - 1 ? '#ccc' : '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: currentReportIndex >= weeklyReports.length - 1 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      ← 이전
                    </button>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                      {selectedWeeklyReport.title} ({currentReportIndex + 1} / {weeklyReports.length})
                    </div>
                    <button
                      onClick={() => navigateWeeklyReport('next')}
                      disabled={currentReportIndex === 0}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: currentReportIndex === 0 ? '#ccc' : '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: currentReportIndex === 0 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      다음 →
                    </button>
                  </div>
                )}
                
                <h2 style={{ marginTop: 0, marginBottom: '20px' }}>
                  {selectedWeeklyReport.title}
                </h2>
                
                {selectedWeeklyReport.summary && (
                  <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px' }}>
                    <h3 style={{ marginTop: 0, fontSize: '16px' }}>📌 요약</h3>
                    <div style={{ fontSize: '14px' }}>{selectedWeeklyReport.summary}</div>
                  </div>
                )}
                
                {selectedWeeklyReport.statistics && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>📊 통계</h3>
                    <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                      <div>총 분석 건수: {selectedWeeklyReport.statistics.total_analyses}건</div>
                      <div>분석 종목 수: {selectedWeeklyReport.symbol_summaries?.length || 0}개</div>
                      <div>평균 신뢰도: {selectedWeeklyReport.statistics.average_confidence}%</div>
                      <div>매수 추천: {selectedWeeklyReport.statistics.buy_recommendations}건</div>
                      <div>매도 추천: {selectedWeeklyReport.statistics.sell_recommendations}건</div>
                      <div>보유 추천: {selectedWeeklyReport.statistics.hold_recommendations}건</div>
                      {selectedWeeklyReport.statistics.failed_analyses > 0 && (
                        <div style={{ color: '#dc3545', fontWeight: '600' }}>
                          실패 분석: {selectedWeeklyReport.statistics.failed_analyses}건
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {selectedWeeklyReport.content && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>📄 상세 리포트</h3>
                    <div style={{
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                      whiteSpace: 'pre-wrap',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      maxHeight: '400px',
                      overflow: 'auto'
                    }}>
                      {selectedWeeklyReport.content.split('\n').map((line, idx) => {
                        if (line.startsWith('# ')) {
                          return <h1 key={idx} style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '15px', marginBottom: '10px' }}>{line.substring(2)}</h1>;
                        } else if (line.startsWith('## ')) {
                          return <h2 key={idx} style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '12px', marginBottom: '8px' }}>{line.substring(3)}</h2>;
                        } else if (line.startsWith('### ')) {
                          return <h3 key={idx} style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '10px', marginBottom: '6px' }}>{line.substring(4)}</h3>;
                        } else if (line.startsWith('- ')) {
                          return <div key={idx} style={{ marginLeft: '20px', marginTop: '4px' }}>{line.substring(2)}</div>;
                        } else if (line.startsWith('**') && line.endsWith('**')) {
                          return <div key={idx} style={{ fontWeight: 'bold', marginTop: '8px' }}>{line.replace(/\*\*/g, '')}</div>;
                        } else if (line.trim() === '') {
                          return <br key={idx} />;
                        } else {
                          return <div key={idx} style={{ marginTop: '4px' }}>{line}</div>;
                        }
                      })}
                    </div>
                  </div>
                )}
                
                <div style={{ fontSize: '12px', color: '#999', textAlign: 'right' }}>
                  생성 시간: {new Date(selectedWeeklyReport.createdAt).toLocaleString('ko-KR')}
                </div>
              </>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                <div>주단위 리포트를 생성해주세요.</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 분석 상세 모달 */}
      {showAnalysisDetail && selectedAnalysis && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 100011,
            padding: '20px'
          }}
          onClick={() => setShowAnalysisDetail(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '25px',
              maxWidth: '800px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAnalysisDetail(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>

            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>
              {selectedAnalysis.stock_name || selectedAnalysis.symbol} - 상세 분석
            </h2>

            {/* 한줄 요약 */}
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px' }}>
              <h3 style={{ marginTop: 0, fontSize: '16px' }}>📌 요약</h3>
              <div style={{ fontSize: '15px', fontWeight: '600' }}>{selectedAnalysis.summary}</div>
            </div>

            {/* 현재 상태 */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>현재 상태</h3>
              <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                <div><strong>종목:</strong> {selectedAnalysis.stock_name || selectedAnalysis.symbol}</div>
                <div><strong>상태:</strong> {selectedAnalysis.current_status || '관찰중'}</div>
                <div><strong>분석 타입:</strong> {
                  selectedAnalysis.analysis_type === 'combined' ? '로컬 AI + Google AI' :
                  selectedAnalysis.analysis_type === 'local_ai' ? '로컬 AI' :
                  selectedAnalysis.analysis_type === 'google_ai' ? 'Google AI' :
                  '알 수 없음'
                }</div>
              </div>
            </div>

            {/* AI 추천 */}
            {selectedAnalysis.recommendation && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>AI 추천</h3>
                <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                  <div><strong>액션:</strong> {
                    selectedAnalysis.recommendation.action === 'buy' ? '매수' :
                    selectedAnalysis.recommendation.action === 'sell' ? '매도' :
                    '보유'
                  }</div>
                  <div><strong>신뢰도:</strong> {selectedAnalysis.recommendation.confidence || 0}%</div>
                  {selectedAnalysis.recommendation.target_price > 0 && (
                    <div><strong>목표가:</strong> ₩{selectedAnalysis.recommendation.target_price.toLocaleString()}</div>
                  )}
                  {selectedAnalysis.recommendation.stop_loss_price > 0 && (
                    <div><strong>손절가:</strong> ₩{selectedAnalysis.recommendation.stop_loss_price.toLocaleString()}</div>
                  )}
                  {selectedAnalysis.recommendation.reasoning && (
                    <div style={{ marginTop: '10px' }}>
                      <strong>이유:</strong>
                      {Array.isArray(selectedAnalysis.recommendation.reasoning) ? (
                        <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                          {selectedAnalysis.recommendation.reasoning.map((reason, idx) => (
                            <li key={idx}>{reason}</li>
                          ))}
                        </ul>
                      ) : (
                        <div style={{ marginTop: '5px' }}>{selectedAnalysis.recommendation.reasoning}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 거래 설정 */}
            {selectedAnalysis.trade_config && selectedAnalysis.trade_config.order_type !== 'none' && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>거래 설정</h3>
                <div style={{ padding: '12px', backgroundColor: '#fff3cd', borderRadius: '6px' }}>
                  <div><strong>주문 유형:</strong> {
                    selectedAnalysis.trade_config.order_type === 'buy' ? '매수' : '매도'
                  }</div>
                  <div><strong>수량:</strong> {selectedAnalysis.trade_config.quantity}주</div>
                  <div><strong>가격:</strong> ₩{selectedAnalysis.trade_config.price?.toLocaleString() || '0'}</div>
                  <div><strong>주문 방법:</strong> {
                    selectedAnalysis.trade_config.order_method === 'limit' ? '지정가' : '시장가'
                  }</div>
                  {selectedAnalysis.trade_config.scheduled_time && (
                    <div><strong>예약 시간:</strong> {new Date(selectedAnalysis.trade_config.scheduled_time).toLocaleString('ko-KR')}</div>
                  )}
                  {selectedAnalysis.scheduled_order_id && (
                    <div><strong>예약 주문:</strong> 연결됨</div>
                  )}
                </div>
              </div>
            )}

            {(selectedAnalysis.recommendation?.action === 'buy' || selectedAnalysis.recommendation?.action === 'sell') && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>거래 실행</h3>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: '12px', color: '#555' }}>
                    예약 시간
                    <input
                      type="datetime-local"
                      value={analysisScheduleTime}
                      onChange={(e) => setAnalysisScheduleTime(e.target.value)}
                      style={{ marginTop: '4px', padding: '6px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                  </label>
                  <button
                    onClick={() => handleScheduleFromAnalysis(selectedAnalysis)}
                    disabled={loading}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: loading ? '#ccc' : '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    📅 예약 주문 생성
                  </button>
                  <button
                    onClick={() => handleExecuteFromAnalysis(selectedAnalysis)}
                    disabled={loading}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: loading ? '#ccc' : '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    ⚡ 즉시 실행
                  </button>
                </div>
              </div>
            )}

            {/* 상세 분석글 */}
            {selectedAnalysis.detailed_analysis && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>상세 분석</h3>
                <div style={{
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  whiteSpace: 'pre-wrap',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  maxHeight: '300px',
                  overflow: 'auto'
                }}>
                  {selectedAnalysis.detailed_analysis}
                </div>
              </div>
            )}

            {/* 현재 설정 결론 (최종 분석글) */}
            {selectedAnalysis.final_conclusion && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>최종 분석 결론</h3>
                <div style={{
                  padding: '15px',
                  backgroundColor: '#e7f3ff',
                  borderRadius: '6px',
                  whiteSpace: 'pre-wrap',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  maxHeight: '400px',
                  overflow: 'auto'
                }}>
                  {selectedAnalysis.final_conclusion}
                </div>
              </div>
            )}

            {/* 계획 */}
            {selectedAnalysis.plan && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>계획</h3>
                <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                  {selectedAnalysis.plan.strategy && (
                    <div><strong>전략:</strong> {selectedAnalysis.plan.strategy}</div>
                  )}
                  {selectedAnalysis.plan.timeline && (
                    <div><strong>타임라인:</strong> {selectedAnalysis.plan.timeline}</div>
                  )}
                  {selectedAnalysis.plan.key_factors && selectedAnalysis.plan.key_factors.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <strong>주요 요인:</strong>
                      <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                        {selectedAnalysis.plan.key_factors.map((factor, idx) => (
                          <li key={idx}>{factor}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedAnalysis.plan.risk_factors && selectedAnalysis.plan.risk_factors.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <strong>리스크 요인:</strong>
                      <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                        {selectedAnalysis.plan.risk_factors.map((risk, idx) => (
                          <li key={idx}>{risk}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 분석 시간 */}
            <div style={{ fontSize: '12px', color: '#999', textAlign: 'right' }}>
              분석 시간: {new Date(selectedAnalysis.createdAt).toLocaleString('ko-KR')}
              {selectedAnalysis.next_analysis_time && (
                <div>다음 분석: {new Date(selectedAnalysis.next_analysis_time).toLocaleString('ko-KR')}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 설정 요약 모달 */}
      {showConfigSummaryModal && (
        <AITradeConfigSummaryModal
          onClose={() => setShowConfigSummaryModal(false)}
          authenticatedFetch={authenticatedFetch}
          user={user}
        />
      )}
    </div>
  );
};

export default AITradeTab;

